import {ArrowDownUp, Rocket, Search, Star} from 'lucide-react'
import { useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import CreateSimLaunchForm from '@/components/CreateSimLaunchFormLazy'
import Layout from '@/components/Layout'
import SeoHead from '@/components/SeoHead'
import SimLaunchCard from '@/components/SimLaunchCard'
import { useSimLaunchpad } from '@/hooks/useSimLaunchpad'
import { useWatchlist } from '@/hooks/useWatchlist'
import { prefetchProjectLinks } from '@/lib/projectLinks'
import {
  CREATOR_SHARE_BPS,
  GRADUATION_QUOTE_ETH,
  LAUNCH_FEE_ETH,
  PLATFORM_SHARE_BPS,
  TRADE_FEE_BPS,
} from '@/lib/launchpad/constants'
import { weiToEthString } from '@/lib/launchpad/wei'
import { formatBps } from '@/lib/protocol'

type SimSortOption = 'newest' | 'most-raised' | 'closest-grad' | 'graduated'
type SimStatusFilter = 'all' | 'live' | 'graduated'

const simSortOptions: { value: SimSortOption, label: string }[] = [
  { value: 'newest', label: 'Newest' },
  { value: 'most-raised', label: 'Most raised' },
  { value: 'closest-grad', label: 'Closest to grad' },
  { value: 'graduated', label: 'Graduated' },
]

const simStatusFilters: { value: SimStatusFilter, label: string }[] = [
  { value: 'all', label: 'All' },
  { value: 'live', label: 'Live' },
  { value: 'graduated', label: 'Graduated' },
]

function Launchpad() {
  const { launches, walletWei } = useSimLaunchpad()
  const { ids: watchIds, isWatched } = useWatchlist()
  const [search, setSearch] = useState('')
  const [watchedOnly, setWatchedOnly] = useState(false)
  const [simSort, setSimSort] = useState<SimSortOption>('newest')
  const [statusFilter, setStatusFilter] = useState<SimStatusFilter>('all')

  const simLaunches = useMemo(() => {
    const query = search.trim().toLowerCase()
    const result = launches.filter((launch) => {
      const isGraduated = launch.status === 'graduated'
      const matchesStatus = statusFilter === 'all'
        || (statusFilter === 'live' && !isGraduated)
        || (statusFilter === 'graduated' && isGraduated)
      const matchesWatched = !watchedOnly || isWatched(launch.id)
      const matchesSearch = !query
        || launch.name.toLowerCase().includes(query)
        || launch.ticker.toLowerCase().includes(query)
      const hideGraduatedFromMostRaised = simSort === 'most-raised'
        && statusFilter !== 'graduated'
        && isGraduated
      const hideLiveFromGraduatedSort = simSort === 'graduated' && !isGraduated
      return matchesStatus && matchesWatched && matchesSearch && !hideGraduatedFromMostRaised && !hideLiveFromGraduatedSort
    })
    const sorted = [...result]
    if (simSort === 'newest') {
      sorted.sort((a, b) => b.createdAtMs - a.createdAtMs)
    }
    else if (simSort === 'most-raised' || simSort === 'graduated') {
      sorted.sort((a, b) => Number(b.realQuoteRaised - a.realQuoteRaised))
    }
    else {
      sorted.sort((a, b) => {
        if (a.status !== b.status)
          return a.status === 'curve' ? -1 : 1
        return Number(b.realQuoteRaised - a.realQuoteRaised)
      })
    }
    return sorted
  }, [launches, search, watchedOnly, isWatched, watchIds, simSort, statusFilter])

  useEffect(() => {
    prefetchProjectLinks(simLaunches.flatMap(launch => [
      launch.contractAddress,
      launch.shareSlug,
      launch.id,
      launch.tokenAddress,
    ]))
  }, [simLaunches])

  return (
    <Layout>
      <SeoHead
        title="Launchpad — Bonding Curve Token Launches"
        description="Browse live, upcoming, and completed bonding curve token launches on Penguin Pad. Fair-launch price discovery with no pre-mines."
      />

      <section className="mx-auto max-w-7xl px-4 pb-4 pt-10 sm:px-6 lg:px-8">
        <div className="flex items-center gap-3">
          <div className="flex h-12 w-12 items-center justify-center rounded-2xl border border-lime-400/20 bg-lime-400/10 text-lime-400">
            <Rocket className="h-5 w-5" aria-hidden="true" />
          </div>
          <div>
            <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-lime-400">Fair launch</p>
            <h1 className="text-3xl font-bold text-white sm:text-4xl">Launchpad</h1>
            <p className="mt-1 max-w-2xl text-sm text-zinc-400">
              Connect on Robinhood Chain to launch. Fee {LAUNCH_FEE_ETH} ETH. Graduates at {GRADUATION_QUOTE_ETH} ETH. Trade fee {formatBps(TRADE_FEE_BPS)}, split {formatBps(CREATOR_SHARE_BPS)} creator / {formatBps(PLATFORM_SHARE_BPS)} platform.
            </p>
          </div>
        </div>
        <div className="mt-5 flex flex-wrap items-center gap-3">
          <a
            href="#create-curve"
            className="inline-flex items-center rounded-full bg-lime-400 px-4 py-2 text-sm font-semibold text-black hover:bg-lime-300"
          >
            Launch a curve
          </a>
          <Link to="/submit?type=launchpad" className="text-sm font-medium text-zinc-400 hover:text-white">
            Or open the full launch form
          </Link>
          <p className="ml-auto text-xs text-zinc-500">
            Local quote cache {weiToEthString(walletWei, 5)} ETH
          </p>
        </div>
      </section>

      <section id="create-curve" className="mx-auto max-w-7xl scroll-mt-24 px-4 pb-2 sm:px-6 lg:px-8">
        <div className="rounded-3xl border border-white/10 bg-white/[0.03] p-6 backdrop-blur-md">
          <h2 className="text-sm font-semibold text-white">Create a curve</h2>
          <p className="mt-1 text-xs text-zinc-500">Requires a connected Robinhood Chain wallet. Pays {LAUNCH_FEE_ETH} ETH. Fee never enters curve reserves.</p>
          <div className="mt-5">
            <CreateSimLaunchForm walletWei={walletWei} />
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8">
        <div className="rounded-3xl border border-white/10 bg-white/[0.03] p-4 backdrop-blur-md sm:p-5">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div className="relative w-full sm:max-w-xs">
              <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-zinc-500" aria-hidden="true" />
              <input
                type="search"
                value={search}
                onChange={e => setSearch(e.target.value)}
                placeholder="Search curves or ticker"
                aria-label="Search launchpad curves"
                className="w-full rounded-full border border-white/10 bg-black/40 py-2.5 pl-9 pr-3 text-sm text-white placeholder:text-zinc-500 focus:border-lime-400/50 focus:outline-none focus:ring-1 focus:ring-lime-400/30"
              />
            </div>
            <div className="flex flex-wrap items-center gap-1.5">
              <div className="flex gap-1.5 overflow-x-auto" role="tablist" aria-label="Filter by status">
                {simStatusFilters.map(filter => (
                  <button
                    key={filter.value}
                    type="button"
                    role="tab"
                    aria-selected={statusFilter === filter.value}
                    onClick={() => {
                      setStatusFilter(filter.value)
                      if (filter.value !== 'graduated' && simSort === 'graduated')
                        setSimSort('newest')
                    }}
                    className={`shrink-0 rounded-full px-3.5 py-2 text-sm font-medium transition-colors ${
                      statusFilter === filter.value
                        ? 'bg-lime-400 text-black'
                        : 'border border-white/10 text-zinc-400 hover:text-white'
                    }`}
                  >
                    {filter.label}
                  </button>
                ))}
              </div>
              <button
                type="button"
                aria-pressed={watchedOnly}
                onClick={() => setWatchedOnly(value => !value)}
                className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-medium transition-colors ${
                  watchedOnly
                    ? 'bg-lime-400 text-black'
                    : 'border border-white/10 text-zinc-500 hover:text-white'
                }`}
              >
                <Star className={`h-3.5 w-3.5 ${watchedOnly ? 'fill-current' : ''}`} aria-hidden="true" />
                Watched
                {watchIds.length > 0 ? ` (${watchIds.length})` : ''}
              </button>
            </div>
          </div>
        </div>

        <div className="mt-8">
          <div className="flex flex-wrap items-end justify-between gap-3">
            <div>
              <h2 className="text-sm font-semibold text-white">
                {statusFilter === 'graduated' || simSort === 'graduated'
                  ? 'Graduated'
                  : statusFilter === 'live'
                    ? 'Live curves'
                    : 'Curves'}
              </h2>
              <p className="mt-1 text-xs text-zinc-500">
                {statusFilter === 'graduated' || simSort === 'graduated'
                  ? 'Tokens that already hit the curve target and seeded LP.'
                  : statusFilter === 'live'
                    ? 'Live curves still filling toward graduation.'
                    : 'Live curves still filling. Graduated tokens sit in Graduated, not Most raised.'}
              </p>
            </div>
            <div className="flex items-center gap-2">
              <ArrowDownUp className="h-3.5 w-3.5 text-zinc-500" aria-hidden="true" />
              <label htmlFor="sim-curve-sort" className="text-xs text-zinc-500">Sort curves</label>
              <select
                id="sim-curve-sort"
                value={simSort}
                onChange={(e) => {
                  const value = e.target.value as SimSortOption
                  setSimSort(value)
                  if (value === 'graduated')
                    setStatusFilter('graduated')
                }}
                className="rounded-full border border-white/10 bg-black/40 px-2.5 py-1.5 text-xs font-medium text-white focus:border-lime-400/50 focus:outline-none focus:ring-1 focus:ring-lime-400/30"
              >
                {simSortOptions.map(o => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>
            </div>
          </div>
          {simLaunches.length > 0
            ? (
                <div className="mt-4 grid grid-cols-2 gap-3 lg:grid-cols-3 lg:gap-5">
                  {simLaunches.map(launch => (
                    <SimLaunchCard key={launch.id} launch={launch} />
                  ))}
                </div>
              )
            : (
                <p className="mt-4 rounded-2xl border border-dashed border-white/10 px-4 py-8 text-center text-sm text-zinc-500">
                  {watchedOnly
                    ? 'No watched curves match this search. Star a listing, then filter Watched.'
                    : statusFilter === 'graduated' || simSort === 'graduated'
                      ? 'No graduated curves match this search.'
                      : `No curves yet. Create one above — launch fee ${LAUNCH_FEE_ETH} ETH on-chain.`}
                </p>
              )}
        </div>
      </section>
    </Layout>
  )
}

export default Launchpad
